/**
 * Типы доски машин — зеркало ответов бэкенда (src/car/car.view.ts,
 * src/car/car.stages.ts). Справочник колонок и вех фронт не заводит:
 * он приходит с GET /api/board.
 */

export type StageSort =
    | "created_at_asc"
    | "stage_entered_at_asc"
    | "stage_entered_at_desc"
    | "take_queue";

export interface IStage {
    key: string;
    title: string;
    order: number;
    /** Колонка с «Взять в работу». */
    takeable: boolean;
    /** Наш этап: уход с него вперёд закрывает его вехи. */
    ours: boolean;
    sort: StageSort;
}

export interface IMilestoneDef {
    key: string;
    title: string;
    /** Этап-«хозяин»: его вехи предлагает окно закрытия этапа. */
    owner_stage: string | null;
    /** Нет — веху не предлагает окно закрытия этапа; подсветка пропуска и подтверждение назад — по `owner_stage`. */
    ask_on_close?: boolean;
}

export type MilestoneStatus = "done" | "issue";

export interface IUserBrief {
    id: string;
    first_name: string;
    last_name: string;
    avatar_path: string | null;
}

export interface ICarColor {
    id: string;
    name: string;
    image_url: string | null;
    hex: string | null;
    sort: number;
    is_active: boolean;
}

export interface ICarMilestone {
    kind: string;
    status: MilestoneStatus;
    remark: string | null;
    done_by: IUserBrief | null;
    done_at: string;
}

export interface ICarAlert {
    reason: string;
    raised_by: IUserBrief | null;
    raised_at: string | null;
    taken_by: IUserBrief | null;
    taken_at: string | null;
}

export interface ICar {
    id: string;
    vin: string;
    stage: string;
    stage_entered_at: string;
    stage_note: string | null;
    /** «Стенды» (S18): на стендах (true) или в ремзоне. */
    at_stands: boolean;
    color_id: string | null;
    color: ICarColor | null;
    taken_by: IUserBrief | null;
    taken_at: string | null;
    alert: ICarAlert | null;
    milestones: ICarMilestone[];
    created_by: IUserBrief | null;
    created_at: string;
    updated_at: string;
    /** Последнее сообщение треда, любое (S3). */
    last_message_at?: string | null;
    /** Сообщений людей в треде — «Обсуждение · {N}» (S3). */
    message_count?: number;
    /** Есть ли загруженный файл этого вида (S15). */
    snapshots: {dids: boolean; dtc: boolean};
    /** Замечания ФЧ по статусу (S21.1, `CarView.fch`): бейдж вкладки, подпись вехи, окно закрытия. Необязательно — ради старых фикстур. */
    fch?: {open: number; resolved: number};
    /** Уехала (S17.1): такой машины нет на доске, она в Стате. Бэк присылает всегда; необязательны — ради старых фикстур тестов. */
    shipped_at?: string | null;
    shipped_by?: IUserBrief | null;
}

export interface ICarEvent {
    id: string;
    car_id: string | null;
    vin: string;
    type: string;
    payload: Record<string, any>;
    user: IUserBrief | null;
    created_at: string;
}

export interface IBoard {
    stages: IStage[];
    milestones: IMilestoneDef[];
    colors: ICarColor[];
    cars: ICar[];
}

export interface ICarWithEvents extends ICar {
    events: ICarEvent[];
}

export interface IMilestoneInput {
    kind: string;
    status: MilestoneStatus;
    remark?: string | null;
}

export interface IMoveInput {
    to_stage: string;
    from_stage: string;
    milestones?: IMilestoneInput[];
    reason?: string | null;
}

export const DONE_STAGE = "done";
export const REWORK_STAGE = "rework";
export const STANDS_STAGE = "stands";
export const DOOR_CALIBRATION = "door_calibration";
/** Сколько завершённых приходит с доской и с каждой «Показать ещё». */
export const DONE_PAGE = 20;
/** Строк журнала за один запрос. */
export const EVENTS_PAGE = 50;
